import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SemanticTableService from '../../services/SemanticTableService';
import HelpIcon from '../help/HelpIcon';
import AccountUsersAutocomplete from '../common/AccountUsersAutocomplete';

const HELP_TOPIC = { module: 'modelo-semantico', topic: 'modelo-semantico' };

const MODEL_TYPES = [
  { value: 'DATABASE', label: 'Base de datos' },
  { value: 'API', label: 'API' },
  { value: 'FILE', label: 'Archivo' },
  { value: 'CONNECTION', label: 'Conexión' }
];

const defaultFormValues = {
  semantic_model_id: '',
  name: '',
  description: '',
  model_type: 'DATABASE',
  is_public: false,
  is_active: true,
  version_id: '',
  semantid_database_id: '',
  containedTables: [],
  usersAllowed: [],
  softDelete: {
    deleted: false,
    deleted_at: null
  }
};

const SemanticModelFormDialog = ({
  open,
  mode = 'create',
  initialValues,
  databases = [],
  onClose,
  onSubmit,
  isSaving,
  onRefreshDatabases,
  isRefreshingDatabases = false,
  userAccountId,
  accountId
}) => {
  const [formValues, setFormValues] = useState(() => ({
    ...defaultFormValues,
    softDelete: { ...defaultFormValues.softDelete }
  }));
  const [errors, setErrors] = useState({});
  const [availableTables, setAvailableTables] = useState([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [tablesError, setTablesError] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  const databaseOptions = useMemo(() => databases || [], [databases]);
  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (!open) {
      setFormValues({
        ...defaultFormValues,
        softDelete: { ...defaultFormValues.softDelete }
      });
      setTableSearch('');
      setErrors({});
      return;
    }

    if (initialValues) {
      setFormValues({
        semantic_model_id:
          initialValues.semantic_model_id ||
          initialValues.semanticModelId ||
          initialValues.ID ||
          initialValues.id ||
          '',
        name: initialValues.name || initialValues.MODEL_NAME || '',
        description: initialValues.description || initialValues.DESCRIPTION || '',
        model_type: initialValues.model_type || initialValues.MODEL_TYPE || 'DATABASE',
        is_public: !!(initialValues.is_public ?? initialValues.IS_PUBLIC),
        is_active: initialValues.is_active !== false && initialValues.IS_ACTIVE !== false,
        version_id: initialValues.version_id || initialValues.VERSION_ID || '',
        semantid_database_id:
          initialValues.semantid_database_id ||
          initialValues.semantic_database_id ||
          initialValues.SEMANTID_DATABASE_ID ||
          initialValues.SEMANTIC_DATABASE_ID ||
          '',
        containedTables: Array.isArray(initialValues.contained_tables_id)
          ? initialValues.contained_tables_id
              .map((item) => {
                if (!item) {
                  return null;
                }
                if (typeof item === 'string') {
                  return item;
                }
                if (item.table_id) {
                  return item.table_id;
                }
                if (item.TABLE_ID) {
                  return item.TABLE_ID;
                }
                return null;
              })
              .filter(Boolean)
          : [],
        usersAllowed: Array.isArray(initialValues.users_allowed)
          ? initialValues.users_allowed
              .map((item) => {
                if (!item) {
                  return null;
                }
                if (typeof item === 'string') {
                  return item;
                }
                if (item.user_id) {
                  return item.user_id;
                }
                if (item.USER_ID) {
                  return item.USER_ID;
                }
                if (item.id) {
                  return item.id;
                }
                return null;
              })
              .filter(Boolean)
          : [],
        softDelete: {
          deleted: !!(
            initialValues.soft_delete?.deleted ??
            initialValues.softDelete?.deleted ??
            initialValues.SOFT_DELETE?.DELETED
          ),
          deleted_at:
            initialValues.soft_delete?.deleted_at ??
            initialValues.softDelete?.deleted_at ??
            initialValues.SOFT_DELETE?.DELETED_AT ??
            null
        }
      });
    } else {
      setFormValues({
        ...defaultFormValues,
        softDelete: { ...defaultFormValues.softDelete }
      });
    }

    setTableSearch('');
    setErrors({});
  }, [open, initialValues]);

  useEffect(() => {
    if (!open) {
      setIsLoadingTables(false);
      setAvailableTables([]);
      setTablesError('');
      return;
    }

    const databaseId = formValues.semantid_database_id;
    if (!databaseId) {
      setIsLoadingTables(false);
      setAvailableTables([]);
      setTablesError('');
      return;
    }

    let cancelled = false;
    const loadTables = async () => {
      setIsLoadingTables(true);
      setTablesError('');
      try {
        const effectiveAccountId = accountId || userAccountId;
        const response = await SemanticTableService.list({
          semanticDatabaseId: databaseId,
          accountId: effectiveAccountId,
          includeDeleted: false
        });

        if (cancelled) {
          return;
        }

        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.semantic_tables)
              ? response.semantic_tables
              : Array.isArray(response)
                ? response
                : [];

        setAvailableTables(items.filter((item) => item && item.table_id));
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('SemanticModelFormDialog: error cargando tablas disponibles', error);
        setAvailableTables([]);
        setTablesError(error.message || 'No fue posible cargar las tablas disponibles.');
      } finally {
        if (!cancelled) {
          setIsLoadingTables(false);
        }
      }
    };

    loadTables();

    return () => {
      cancelled = true;
      setIsLoadingTables(false);
    };
  }, [open, formValues.semantid_database_id, accountId, userAccountId]);

  const tablesIndex = useMemo(() => {
    const index = new Map();
    (availableTables || []).forEach((table) => {
      if (table?.table_id) {
        index.set(table.table_id, table);
      }
    });
    return index;
  }, [availableTables]);

  const selectedTableOptions = useMemo(
    () =>
      (formValues.containedTables || []).map((tableId) => {
        return (
          tablesIndex.get(tableId) || {
            table_id: tableId,
            table_name: tableId
          }
        );
      }),
    [formValues.containedTables, tablesIndex]
  );

  const validate = () => {
    const currentErrors = {};

    if (!formValues.name.trim()) {
      currentErrors.name = 'Ingresa un nombre para el modelo';
    } else if (formValues.name.trim().length < 3) {
      currentErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }

    if (!formValues.semantid_database_id) {
      currentErrors.semantid_database_id = 'Selecciona una base semántica';
    }

    if (!userAccountId) {
      currentErrors.user_accout_id = 'No se pudo identificar la cuenta asociada';
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleChange = (key) => (event) => {
    const { value, checked, type } = event.target;
    setFormValues((prev) => {
      const nextValue = type === 'checkbox' ? checked : value;
      if (key === 'semantid_database_id') {
        return {
          ...prev,
          semantid_database_id: nextValue,
          containedTables: []
        };
      }
      return {
        ...prev,
        [key]: nextValue
      };
    });
    if (key === 'semantid_database_id') {
      setTableSearch('');
    }
  };

  const handleSoftDeleteToggle = (_, checked) => {
    setFormValues((prev) => ({
      ...prev,
      softDelete: {
        deleted: checked,
        deleted_at: checked ? prev.softDelete?.deleted_at || null : null
      }
    }));
  };

  const handleUsersChange = (usersAllowed) => {
    setFormValues((prev) => ({ ...prev, usersAllowed }));
  };

  const getTableOptionLabel = useCallback((option) => {
    if (!option) {
      return '';
    }
    return (
      option.display_name ||
      option.table_name ||
      option.table_id ||
      ''
    );
  }, []);

  const filterTableOptions = useCallback(
    (options, { inputValue }) => {
      const search = inputValue.trim().toLowerCase();
      if (!search || search.length < 3) {
        return options;
      }

      return options.filter((option) => {
        const label = `${option.display_name || ''} ${option.table_name || ''} ${option.table_id || ''}`.toLowerCase();
        return label.includes(search);
      });
    },
    []
  );

  const handleTableSelectionChange = (_, newOptions) => {
    setFormValues((prev) => ({
      ...prev,
      containedTables: newOptions
        .map((option) => option.table_id)
        .filter(Boolean)
    }));
  };

  const hasDatabaseSelected = Boolean(formValues.semantid_database_id);

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const basePayload = {
      name: formValues.name.trim(),
      description: formValues.description?.trim() || '',
      is_public: !!formValues.is_public,
      is_active: !!formValues.is_active,
      semantid_database_id: formValues.semantid_database_id,
      user_accout_id: userAccountId,
      version_id: formValues.version_id ? formValues.version_id.trim() : undefined
    };

    if (formValues.containedTables.length > 0) {
      basePayload.contained_tables_id = formValues.containedTables.map((tableId) => ({
        table_id: tableId
      }));
    }

    if (formValues.usersAllowed.length > 0) {
      basePayload.users_allowed = formValues.usersAllowed.map((userId) => ({
        user_id: userId
      }));
    }

    if (isEditMode) {
      basePayload.semantic_model_id = formValues.semantic_model_id;
    }

    if (formValues.softDelete.deleted || isEditMode) {
      basePayload.soft_delete = {
        deleted: !!formValues.softDelete.deleted,
        deleted_at: formValues.softDelete.deleted
          ? formValues.softDelete.deleted_at || null
          : null
      };
    }

    if (!basePayload.version_id) {
      delete basePayload.version_id;
    }

    onSubmit?.(basePayload);
  };

  const handleRefreshDatabases = async () => {
    if (onRefreshDatabases) {
      await onRefreshDatabases();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-panel)',
          color: 'var(--wb-text-strong)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
        {isEditMode ? 'Editar modelo semántico' : 'Nuevo modelo semántico'}
      </DialogTitle>
      {/* '&&' gana a la regla de MUI que quita el padding superior tras un
          DialogTitle; sin ese espacio la etiqueta de "Base semántica" queda
          cortada contra el encabezado del diálogo. */}
      <DialogContent sx={{ '&&': { pt: 3 } }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <FormControl
              fullWidth
              error={Boolean(errors.semantid_database_id)}
              sx={{ flexGrow: 1, minWidth: 220 }}
            >
              <InputLabel id="semantic-model-database-label" sx={{ color: 'var(--wb-text-label)' }}>
                Base semántica
              </InputLabel>
              <Select
                labelId="semantic-model-database-label"
                value={formValues.semantid_database_id}
                onChange={handleChange('semantid_database_id')}
                label="Base semántica"
                sx={{ color: 'var(--wb-text)' }}
              >
                {databaseOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    No hay bases semánticas disponibles
                  </MenuItem>
                )}
                {databaseOptions.map((database, index) => {
                  const optionValue =
                    database?.semantic_database_id ||
                    database?.semanticDatabaseId ||
                    database?.id ||
                    database?.ID ||
                    `database-${index}`;
                  const optionLabel = database?.name || database?.NAME || optionValue;

                  return (
                    <MenuItem key={`${optionValue}-${index}`} value={optionValue}>
                      {optionLabel}
                    </MenuItem>
                  );
                })}
              </Select>
              {errors.semantid_database_id && (
                <FormHelperText>{errors.semantid_database_id}</FormHelperText>
              )}
            </FormControl>
            <Tooltip title="Actualizar bases semánticas">
              <span>
                <Button
                  variant="outlined"
                  onClick={handleRefreshDatabases}
                  disabled={isRefreshingDatabases || !onRefreshDatabases}
                  startIcon={<RefreshIcon />}
                  sx={{
                    color: 'var(--wb-accent)',
                    borderColor: 'var(--wb-accent-40)',
                    '&:hover': { borderColor: 'var(--wb-accent)' }
                  }}
                >
                  {isRefreshingDatabases ? 'Actualizando…' : 'Actualizar'}
                </Button>
              </span>
            </Tooltip>
            <HelpIcon {...HELP_TOPIC} anchor="base-semántica" />
          </Box>

          <TextField
            label="Nombre del modelo"
            value={formValues.name}
            onChange={handleChange('name')}
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name}
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { color: 'var(--wb-text)' }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="nombre-del-modelo" /></InputAdornment> }}
          />

          <TextField
            label="Descripción"
            value={formValues.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            minRows={3}
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { color: 'var(--wb-text)' }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="descripción" /></InputAdornment> }}
          />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <FormControl fullWidth error={Boolean(errors.model_type)}>
              <InputLabel id="semantic-model-type-label" sx={{ color: 'var(--wb-text-label)' }}>
                Tipo de modelo
              </InputLabel>
              <Select
                labelId="semantic-model-type-label"
                value={formValues.model_type}
                onChange={handleChange('model_type')}
                label="Tipo de modelo"
                sx={{ color: 'var(--wb-text)' }}
              >
                {MODEL_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.model_type && <FormHelperText>{errors.model_type}</FormHelperText>}
            </FormControl>
            <HelpIcon {...HELP_TOPIC} anchor="tipo-de-modelo" sx={{ mt: 1 }} />
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={!!formValues.is_public}
                  onChange={handleChange('is_public')}
                  color="primary"
                />
              }
              label="Modelo público"
            />
            <HelpIcon {...HELP_TOPIC} anchor="modelo-público" />
            <FormControlLabel
              control={
                <Switch
                  checked={!!formValues.is_active}
                  onChange={handleChange('is_active')}
                  color="primary"
                />
              }
              label="Modelo activo"
            />
            <HelpIcon {...HELP_TOPIC} anchor="modelo-activo" />
            {isEditMode && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formValues.softDelete.deleted}
                      onChange={handleSoftDeleteToggle}
                      color="primary"
                    />
                  }
                  label="Marcar como eliminado"
                />
                <HelpIcon {...HELP_TOPIC} anchor="marcar-como-eliminado-solo-al-editar" />
              </>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Versión"
              value={formValues.version_id}
              onChange={handleChange('version_id')}
              InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
              InputProps={{ sx: { color: 'var(--wb-text)' }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="versión" /></InputAdornment> }}
              fullWidth
              sx={{ maxWidth: 240 }}
            />
            {isEditMode && formValues.softDelete.deleted && (
              <TextField
                label="Fecha de eliminación"
                value={formValues.softDelete.deleted_at || ''}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    softDelete: { ...prev.softDelete, deleted_at: event.target.value }
                  }))
                }
                placeholder="ISO timestamp o deja en blanco"
                fullWidth
                InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
                InputProps={{ sx: { color: 'var(--wb-text)' } }}
              />
            )}
          </Stack>

          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--wb-muted-80)' }}>
                Entidades relacionadas
              </Typography>
              <HelpIcon {...HELP_TOPIC} anchor="entidades-relacionadas" />
            </Stack>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!hasDatabaseSelected}
              options={availableTables}
              value={selectedTableOptions}
              onChange={handleTableSelectionChange}
              inputValue={tableSearch}
              onInputChange={(_, newValue) => setTableSearch(newValue || '')}
              getOptionLabel={getTableOptionLabel}
              filterOptions={filterTableOptions}
              isOptionEqualToValue={(option, value) => option.table_id === value.table_id}
              loading={isLoadingTables}
              loadingText="Cargando..."
              noOptionsText={
                !hasDatabaseSelected
                  ? 'Selecciona una base semántica para buscar tablas.'
                  : tableSearch.trim().length < 3
                    ? 'Escribe al menos 3 caracteres para buscar.'
                    : 'No se encontraron tablas.'
              }
              renderOption={(props, option, { selected }) => {
                const secondary = [option.database, option.schema]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li {...props}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 1, color: selected ? 'var(--wb-accent)' : 'var(--wb-x-text-64748b, #64748b)' }}
                    />
                    <Box>
                      <Typography sx={{ color: 'var(--wb-text)' }}>
                        {getTableOptionLabel(option)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--wb-muted-75)' }}>
                        {secondary || `ID: ${option.table_id}`}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Entidades relacionadas"
                  placeholder="Busca por nombre de entidad"
                  InputLabelProps={{ ...params.InputLabelProps, sx: { color: 'var(--wb-x-text-9ca3af, #9ca3af)' } }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { color: 'var(--wb-text)' },
                    endAdornment: (
                      <>
                        {isLoadingTables ? (
                          <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                  helperText={
                    !hasDatabaseSelected
                      ? 'Selecciona una base semántica para habilitar la búsqueda.'
                      : tableSearch.trim().length < 3
                        ? 'Escribe al menos 3 caracteres para activar la búsqueda.'
                        : 'Selecciona una o varias entidades.'
                  }
                />
              )}
              sx={{
                '& .MuiAutocomplete-tag': {
                  backgroundColor: 'var(--wb-x-bg-38bdf824, rgba(56, 189, 248, 0.14))',
                  color: 'var(--wb-x-text-bae6fd, #bae6fd)'
                }
              }}
            />
            {tablesError && (
              <Typography variant="body2" color="var(--wb-danger)" sx={{ mt: 1 }}>
                {tablesError}
              </Typography>
            )}
          </Box>

          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--wb-text-label)' }}>
                Usuarios autorizados
              </Typography>
              <HelpIcon {...HELP_TOPIC} anchor="usuarios-autorizados" />
            </Stack>
            <AccountUsersAutocomplete
              enabled={open}
              value={formValues.usersAllowed}
              onChange={handleUsersChange}
              chipSx={{ bgcolor: 'var(--wb-x-bg-818cf824, rgba(129, 140, 248, 0.14))', color: 'var(--wb-x-text-c7d2fe, #c7d2fe)' }}
            />
          </Box>

          {errors.user_accout_id && (
            <Typography variant="body2" color="var(--wb-danger)">
              {errors.user_accout_id}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)' }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ color: 'var(--wb-muted)' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{
            bgcolor: 'var(--wb-indigo)',
            '&:hover': { bgcolor: 'var(--wb-x-bg-4f46e5, #4f46e5)' }
          }}
        >
          {isSaving ? 'Guardando…' : isEditMode ? 'Guardar cambios' : 'Crear modelo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SemanticModelFormDialog;
